import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import * as moment from 'moment';
import { forkJoin, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BookingConfirmation } from '../booking-confirmation';
import { BookingConfirmationTemplate } from '../booking-confirmation-template';
import { BookingDocumentTemplate } from '../booking-document-template';
import { BookingEmailTemplate } from '../booking-email-template';
import { BookingFile } from '../booking-file';
import { BookingFinalConfirmation } from '../booking-final-confirmation';
import { BookingFinalConfirmationTemplate } from '../booking-final-confirmation-template';
import { BookingGlobalFile } from '../booking-global-file';
import { BookingReport } from '../booking-report';
import { BookingReportTemplate } from '../booking-report-template';
import { BookingSchoolFile } from '../booking-school-file';
import { FileType } from '../file-type';
import { GlobalBookingFile } from '../global-booking-file';
import { GmailMessageWithBookingFiles } from '../gmail-message-with-booking-files';
import { HttpService } from '../service/http.service';
import { SchoolFile } from '../school-file';
import { BookingEmailService } from '../service/booking-email.service';
import { BookingViewModeService } from '../service/booking-view-mode.service';
import { BookingService } from '../service/booking.service';
import { EmailSignatureService } from '../service/email-signature.service';
import { SimpleBookingEmailTemplate } from '../simple-booking-email-template';
import { SimpleBookingPreConfirmation } from '../simple-booking-pre-confirmation';
import { SystemUser } from '../system-user';
import { ToastMessageService } from '../toast-message.service';
import { UserService } from '../service/user.service';
import Decimal from 'decimal.js';
import { ConfirmationService } from 'primeng/api';
import * as Editor from '../../libs/ckeditor';

@Component({
  selector: 'app-booking-files',
  templateUrl: './booking-files.component.html',
  styleUrls: ['./booking-files.component.css']
})
export class BookingFilesComponent implements OnInit {

  sendingBookingReportByEmail = false;
  sendingBookingConfirmationByEmail = false;
  sendingBookingPreConfirmationByEmail = false;
  bookingReportEmailForm!: FormGroup;
  bookingConfirmationEmailForm!: FormGroup;
  bookingFinalConfirmationEmailForm!: FormGroup;
  displayPreConfirmationEmailForm = false;
  preConfirmationEmailForm!: FormGroup;

  individualBookingFiles: BookingFile[] = [];
  loadingSelectedEmailTemplate = false;

  importedSchoolFiles: BookingSchoolFile[] = [];
  importedGlobalFiles: BookingGlobalFile[] = [];
  makingBookingReport = false;
  //email sending module (right button 'Send e-mail')

  selectedFileType!: FileType | null;

  attachmentLoading = false;
  displayEmailForm = false;
  displayEmailFormWithBookingConfirmation = false;
  displayEmailFormWithBookingFinalConfirmation = false;
  globalFiles: GlobalBookingFile[] = [];
  loadingGlobalFiles = false;
  globalFilesImportForm: FormGroup[] = [];
  activeGlobalFileImportDialog = false;
  activeGlobalFileToBeCompletedImportDialog = false;
  loadingSchoolFiles = false;
  fileToUpload: File | null = null;
  activeSchoolFileImportDialog = false;
  activeSchoolFileToBeCompletedImportDialog = false;
  schoolFiles: SchoolFile[] = [];
  schoolFilesImportForm: FormGroup[] = [];
  selectedDocumentTemplate!: BookingDocumentTemplate;
  processing = false;
  bookingConfirmations: BookingConfirmation[] = [];
  bookingFinalConfirmations: BookingFinalConfirmation[] = [];

  creatingEmptyIndividualFile = false;
  activeFinalConfEdit = false;
  isFinalConfirmationTemplateSelected = false;
  editableFinalConfirmationHtmlString: string | null = null;
  dropdownScrollHeight: string = "600px";
  vivawalletOrderAmount!: Decimal | null;

  hasVivawalletPaymentOrder = false;
  vivawalletCheckourUrl: string | null = null;

  public Editor = Editor;

  constructor(
    public bookingService: BookingService,
    private httpService: HttpService,
    public userService: UserService,
    private toastService: ToastMessageService,
    private builder: FormBuilder,
    public bookingEmailService: BookingEmailService,
    private confirmationService: ConfirmationService,
    private bookingViewModeService: BookingViewModeService,
    public emailSignatureService: EmailSignatureService
  ) { }

  ngOnInit(): void {
    this.updateSchoolFiles();
    this.updateIndividualFiles();
    this.updateBookingGlobalFiles();
    this.getPreConfirmations();
    this.initBookingConfirmations();
    this.initBookingFinalConfirmations();
  }

  initBookingConfirmations() {
    this.bookingConfirmations = [];
    if (this.bookingService.bookingConfirmations) {
      this.bookingConfirmations = this.bookingService.bookingConfirmations;
    }
  }

  initBookingFinalConfirmations() {
    this.bookingFinalConfirmations = [];
    for (let finalConfirmation of this.bookingService.bookingFinalConfirmations) {
      this.bookingFinalConfirmations.push(finalConfirmation);
    }
  }

  navigateToConfirmationPreview(confirmation: BookingConfirmation) {
    window.open("/management/booking-confirmation-preview/" + confirmation.id, "_blank");
  }

  navigateToFinalConfirmationPreview(finalConfirmation: BookingFinalConfirmation) {
    window.open("/management/booking-final-confirmation-preview/" + finalConfirmation.id, "_blank");
  }

  get studentRecipients(): FormArray {
    return this.bookingReportEmailForm.get("studentRecipients") as FormArray;
  }

  get schoolRecipients(): FormArray {
    return this.bookingReportEmailForm.get("schoolRecipients") as FormArray;
  }

  get finalConfirmationStudentRecipients(): FormArray {
    return this.bookingFinalConfirmationEmailForm.get("recipients") as FormArray;
  }

  get bookingConfirmationStudentRecipients(): FormArray {
    return this.bookingConfirmationEmailForm.get("recipients") as FormArray;
  }

  get bookingPreConfirmationStudentRecipients(): FormArray {
    return this.preConfirmationEmailForm.get("recipients") as FormArray;
  }

  handleFileInput(files: FileList) {
    this.fileToUpload = files.item(0);
  }

  resetEmailForms() {
    if (this.bookingReportEmailForm) {
      this.bookingReportEmailForm.reset();
    }
    if (this.preConfirmationEmailForm) {
      this.preConfirmationEmailForm.reset();
    }
  }

  resetPreConfirmationEmailForm() {
    if (this.preConfirmationEmailForm) {
      this.preConfirmationEmailForm.reset();
    }
  }

  sendPreConfirmationByEmail() {
    if (this.preConfirmationEmailForm.value.withVivawalletPaymentLink) {
      if (!this.hasVivawalletElement(this.preConfirmationEmailForm.value.body)) {
        this.toastService.addError("Error", "This email has no '$vivawallet$' element.");
        return;
      }
    }
    this.sendingBookingPreConfirmationByEmail = true;
    if (!this.bookingService.booking.student.email || this.bookingService.booking.student.email.length < 2) {
      this.sendingBookingPreConfirmationByEmail = false;
      this.toastService.addError("Error", "Please set email address to this student");
      return;
    }
    let recipients = this.getPreConfirmationEmailRecipients();
    if (!recipients || recipients.length == 0) {
      this.toastService.addError("Error", "Please select at least one email address.");
      this.sendingBookingPreConfirmationByEmail = false;
      return;
    }
    let message = new GmailMessageWithBookingFiles(
      recipients,
      this.preConfirmationEmailForm.value.subject,
      this.preConfirmationEmailForm.value.body,
      this.bookingEmailService.emailAttachmentFiles,
      this.preConfirmationEmailForm.value.signature.language.id,
      this.bookingService.booking.id!,
      this.preConfirmationEmailForm.value.withVivawalletPaymentLink,
      null
    );
    let fileName = "pre_confirmation_" + this.bookingService.booking.student.firstName + "_" + this.bookingService.booking.student.lastName;

    message.body = this.getStringWithEscapedDollar(message.body);
    this.sendingBookingPreConfirmationByEmail = true;
    this.sendEmailMessageWithPreConfirmationToServer(message, fileName);

  }

  getStringWithEscapedDollar(str: string): string {
    let newStr = str;
    if (str != null) {
      let dollarIndexes: number[] = [];
      for (let i = 0; i < str.length; i++) {
        if (str[i] === "$") {
          if (!this.isVivawalletDelimitor(str, i)) {
            dollarIndexes.push(i);
          }
        }
      }
      if (dollarIndexes.length > 0) {
        let addedCharCounter = 0;
        for (let i = 0; i < dollarIndexes.length; i++) {
          let dollarPos = dollarIndexes[i];
          if (newStr.substring(dollarPos - 1 + addedCharCounter, dollarPos + 1 + addedCharCounter) != "\\$") {
            newStr = newStr.slice(0, dollarPos + addedCharCounter) + "\\" + newStr.slice(dollarPos + addedCharCounter);
            addedCharCounter++;
          }
        }
      }
    }
    return newStr;
  }

  isVivawalletDelimitor(str: string, index: number): boolean {
    //check if it's the start of $vivawallet$
    let subAfter = str.substring(index, index + 12);
    if (subAfter === "$vivawallet$") {
      return true;
    }
    //check if it's the end of $vivawallet$
    let subBefore = str.substring(index - 11, index + 1);
    if (subBefore === "$vivawallet$") {
      return true;
    }
    return false;
  }

  sendEmailMessageWithPreConfirmationToServer(message: GmailMessageWithBookingFiles, fileName: string) {
    this.httpService.sendBookingPreConfirmationByGmail(message, this.preConfirmationEmailForm.value.preconfId, fileName,
      this.preConfirmationEmailForm.value.withPastePixel)
      .subscribe(() => {
        this.toastService.addSuccess("Success", "Mail sent");
        this.displayPreConfirmationEmailForm = false;
        this.sendingBookingPreConfirmationByEmail = false;
        this.bookingEmailService.emailAttachmentFiles = [];
        this.setPreconfSentInfo(this.preConfirmationEmailForm.value.preconfId);
      }, error => {
        this.sendingBookingPreConfirmationByEmail = false;
        this.toastService.addError("Error", error.error);
        this.bookingEmailService.emailAttachmentFiles = [];
      });
  }

  hasVivawalletElement(emailBody: string) {
    if (emailBody == null) {
      return false;
    }
    if (emailBody.indexOf("$vivawallet$") == -1) {
      return false;
    }
    return true;
  }

  closeEmailDialog() {
    this.bookingEmailService.displayMailingDialog = false;
  }

  setPreconfSentInfo(preconfId: number) {
    for (let preconf of this.bookingService.preConfirmations) {
      if (preconf.id == preconfId) {
        preconf.sentDate = moment();
        preconf.sentBy = new SystemUser(this.userService.userName, this.userService.roles!);
      }
    }
    if (!this.bookingService.bookingForm.value.sentToStudent) {
      this.bookingService.bookingForm.get("preConfirmationSent")!.setValue(new Date());
    }
  }

  getEmailRecipients() {
    let recipients: string[] = [];
    for (let student of this.studentRecipients.controls) {
      if (student.value.isSelected) {
        recipients.push(student.value.email);
      }
    }
    for (let schoolContact of this.schoolRecipients.controls) {
      if (schoolContact.value.isSelected) {
        recipients.push(schoolContact.value.email);
      }
    }
    return recipients;
  }

  getFinalConfirmationEmailRecipients() {
    let recipients: string[] = [];
    for (let student of this.finalConfirmationStudentRecipients.controls) {
      if (student.value.isSelected) {
        recipients.push(student.value.email);
      }
    }
    return recipients;
  }

  getBookingConfirmationEmailRecipients() {
    let recipients: string[] = [];
    for (let student of this.bookingConfirmationStudentRecipients.controls) {
      if (student.value.isSelected) {
        recipients.push(student.value.email);
      }
    }
    return recipients;
  }

  getPreConfirmationEmailRecipients() {
    let recipients: string[] = [];
    for (let student of this.bookingPreConfirmationStudentRecipients.controls) {
      if (student.value.isSelected) {
        recipients.push(student.value.email);
      }
    }
    return recipients;
  }

  selectEmailTemplate(selectedTemplate: BookingEmailTemplate) {
    this.loadingSelectedEmailTemplate = true;
    let template = new SimpleBookingEmailTemplate(
      selectedTemplate.body,
      selectedTemplate.subject,
      selectedTemplate.backgroundColor
    );
    this.httpService.getCompletedBookingEmailTemplate(template, this.bookingService.booking.id!).subscribe(res => {
      if (this.displayEmailForm) {
        this.bookingReportEmailForm.get("subject")!.setValue(res.subject);
        this.bookingReportEmailForm.get("body")!.setValue(res.body);
      }
      else if (this.displayEmailFormWithBookingConfirmation) {
        this.bookingConfirmationEmailForm.get("subject")!.setValue(res.subject);
        this.bookingConfirmationEmailForm.get("body")!.setValue(res.body);
      }
      else if (this.displayEmailFormWithBookingFinalConfirmation) {
        this.bookingFinalConfirmationEmailForm.get("subject")!.setValue(res.subject);
        this.bookingFinalConfirmationEmailForm.get("body")!.setValue(res.body);
      } else if (this.displayPreConfirmationEmailForm) {
        this.preConfirmationEmailForm.get("subject")!.setValue(res.subject);
        this.preConfirmationEmailForm.get("body")!.setValue(res.body);
      }
      this.loadingSelectedEmailTemplate = false;
    }, error => {
      this.toastService.addError("Error", error.error);
      this.loadingSelectedEmailTemplate = false;
    });
  }

  getDefaultValueWithVivawalletPaymentLink() {
    if (this.bookingService.booking.preConfirmation!.selectedBank?.id == 2) {
      return false
    }
    return true;
  }

  createEmailFormWithPreConfirmation(preconf: SimpleBookingPreConfirmation) {
    this.httpService.getBookingPreConfirmationFileName(preconf.id).subscribe(res => {
      let fileName = null;
      if (res == null || res == "") {
        fileName = "PreConfirmation_" + this.bookingService.booking.student.firstName + "_" + this.bookingService.booking.student.lastName + ".pdf";
      } else {
        fileName = res;
      }

      this.preConfirmationEmailForm = this.builder.group({
        subject: "Please find attached...",
        body: "",
        isStudentDocument: true,
        isSchoolDocument: false,
        report: { fileName: fileName },
        preconfId: preconf.id,
        signature: this.emailSignatureService.getEmailSignatureByLanguage(this.bookingService.booking.student.language),
        recipients: this.builder.array([]),
        withVivawalletPaymentLink: this.getDefaultValueWithVivawalletPaymentLink(),
        withPastePixel: true
      });
      this.bookingEmailService.loadEmailTemplate(this.bookingService.booking.student.language.id);
      this.displayPreConfirmationEmailForm = true;
      this.initPreConfirmationContacts();
      if (this.bookingService.booking.preConfirmation!.selectedBank.id != 2) {
        this.updateVivawalletChargeAmount(true, this.preConfirmationEmailForm);
      }
    }, error => {
      console.error(error);
      return;
    });
  }

  createEmailFormWithBookingConfirmation(bookingConfirmation: BookingConfirmation) {
    this.displayEmailFormWithBookingConfirmation = true;
    this.bookingEmailService.loadEmailTemplate(this.bookingService.booking.student.language.id);
    this.bookingConfirmationEmailForm = this.builder.group({
      subject: "Please find your booking confirmation",
      body: "",
      confirmation: bookingConfirmation,
      recipients: this.builder.array([]),
      signature: this.emailSignatureService.getEmailSignatureByLanguage(this.bookingService.booking.student.language),
      withVivawalletPaymentLink: false,
      withPastePixel: true
    });
    this.initBookingConfirmationContacts();
    this.updateVivawalletChargeAmount(this.bookingConfirmationEmailForm.value.withVivawalletPaymentLink, this.bookingConfirmationEmailForm);
  }

  createEmailFormWithBookingFinalConfirmation(finalConfirmation: BookingFinalConfirmation) {
    this.displayEmailFormWithBookingFinalConfirmation = true;
    this.bookingEmailService.loadEmailTemplate(this.bookingService.booking.student.language.id);
    this.bookingFinalConfirmationEmailForm = this.builder.group({
      subject: "Please find your final confirmation",
      body: "",
      finalConfirmation: finalConfirmation,
      recipients: this.builder.array([]),
      signature: this.emailSignatureService.getEmailSignatureByLanguage(this.bookingService.booking.student.language),
      withVivawalletPaymentLink: false,
      withPastePixel: true
    });
    this.initFinalConfirmationContacts();
    this.updateVivawalletChargeAmount(this.bookingFinalConfirmationEmailForm.value.withVivawalletPaymentLink, this.bookingFinalConfirmationEmailForm);
  }

  createEmailFormWithBookingReport(bookingReport: BookingReport) {
    this.bookingReportEmailForm = this.builder.group({
      subject: "Please find attached...",
      body: "",
      report: bookingReport,
      studentRecipients: this.builder.array([]),
      schoolRecipients: this.builder.array([]),
      isStudentDocument: bookingReport.bookingReportTemplate.isStudentDocument,
      isSchoolDocument: bookingReport.bookingReportTemplate.isSchoolDocument,
      withVivawalletPaymentLink: false,
      withPastePixel: true
    });
    this.bookingEmailService.loadEmailTemplate(this.bookingService.booking.student.language.id);

    //documentType.id 1 is for school
    if (bookingReport.bookingReportTemplate.documentType.id == 1) {
      bookingReport.bookingReportTemplate.isSchoolDocument = true;
      this.bookingReportEmailForm.get("isSchoolDocument")!.setValue(true);
      for (let schoolContact of this.bookingEmailService.schoolContacts) {
        this.schoolRecipients.controls.push(this.builder.group({
          email: schoolContact.email,
          isSelected: false,
          typeName: schoolContact.schoolContactType.name
        }));
      }
      //documentType.id 2 is for student
    } else if (bookingReport.bookingReportTemplate.documentType.id == 2) {
      this.updateVivawalletChargeAmount(this.bookingReportEmailForm.value.withVivawalletPaymentLink, this.bookingReportEmailForm);
      bookingReport.bookingReportTemplate.isStudentDocument = true;
      this.bookingReportEmailForm.get("isStudentDocument")!.setValue(true);
      this.initStudentContacts();
    }
    this.displayEmailForm = true;
  }

  initStudentContacts() {
    if (this.bookingService.booking.student.email) {
      this.studentRecipients.controls.push(this.builder.group({
        email: this.bookingService.booking.student.email,
        isSelected: false,
        typeName: this.bookingService.booking.student.primaryContactType.name
      }));
    }
    for (let studentContact of this.bookingService.booking.student.contacts) {
      this.studentRecipients.controls.push(this.builder.group({
        email: studentContact.email,
        isSelected: false,
        typeName: studentContact.type.name
      }));
    }
  }

  initFinalConfirmationContacts() {
    if (this.bookingService.booking.student.email) {
      this.finalConfirmationStudentRecipients.controls.push(this.builder.group({
        email: this.bookingService.booking.student.email,
        isSelected: false,
        typeName: this.bookingService.booking.student.primaryContactType.name
      }));
    }
    for (let studentContact of this.bookingService.booking.student.contacts) {
      this.finalConfirmationStudentRecipients.controls.push(this.builder.group({
        email: studentContact.email,
        isSelected: false,
        typeName: studentContact.type.name
      }));
    }
  }

  initBookingConfirmationContacts() {
    if (this.bookingService.booking.student.email) {
      this.bookingConfirmationStudentRecipients.controls.push(this.builder.group({
        email: this.bookingService.booking.student.email,
        isSelected: false,
        typeName: this.bookingService.booking.student.primaryContactType.name
      }));
    }
    for (let studentContact of this.bookingService.booking.student.contacts) {
      this.bookingConfirmationStudentRecipients.controls.push(this.builder.group({
        email: studentContact.email,
        isSelected: false,
        typeName: studentContact.type.name
      }));
    }
  }

  initPreConfirmationContacts() {
    if (this.bookingService.booking.student.email) {
      this.bookingPreConfirmationStudentRecipients.controls.push(this.builder.group({
        email: this.bookingService.booking.student.email,
        isSelected: false,
        typeName: this.bookingService.booking.student.primaryContactType.name
      }));
    }
    for (let studentContact of this.bookingService.booking.student.contacts) {
      this.bookingPreConfirmationStudentRecipients.controls.push(this.builder.group({
        email: studentContact.email,
        isSelected: false,
        typeName: studentContact.type.name
      }));
    }
  }

  sendBookingConfirmationByMail(confirmation: BookingConfirmation) {
    this.sendingBookingReportByEmail = true;
    let recipients = this.getBookingConfirmationEmailRecipients();
    if (!recipients || recipients.length == 0) {
      this.toastService.addError("Error", "You have to select at least one email address");
      this.sendingBookingConfirmationByEmail = false;
      return;
    }
    let message = new GmailMessageWithBookingFiles(
      recipients,
      this.bookingConfirmationEmailForm.value.subject,
      this.bookingConfirmationEmailForm.value.body,
      this.bookingEmailService.emailAttachmentFiles,
      this.bookingConfirmationEmailForm.value.signature.language.id,
      this.bookingService.booking.id!,
      this.bookingConfirmationEmailForm.value.withVivawalletPaymentLink,
      null
    );
    message.body = this.getStringWithEscapedDollar(message.body);
    this.httpService.sendBookingConfirmationByGmail(message, confirmation.id, this.bookingService.booking.id!,
      this.bookingConfirmationEmailForm.value.withPastePixel)
      .subscribe(() => {
        this.toastService.addSuccess("Success", "Mail sent");
        this.displayEmailFormWithBookingConfirmation = false;
        this.sendingBookingReportByEmail = false;
        this.sendingBookingConfirmationByEmail = false;
        this.bookingEmailService.emailAttachmentFiles = [];
        this.bookingConfirmations[0].lastSent = moment().utc(true).toString();
        this.bookingConfirmations[0].lastSentBy = this.userService.userName!;
        this.bookingService.bookingForm.get("bookingConfirmed")!.setValue(moment().toDate());
      }, error => {
        this.sendingBookingReportByEmail = false;
        this.sendingBookingConfirmationByEmail = false;
        this.toastService.addError("Error", error.error);
        this.bookingEmailService.emailAttachmentFiles = [];
      });
  }

  sendBookingFinalConfirmationByMail(confirmation: BookingFinalConfirmation) {
    this.sendingBookingReportByEmail = true;
    let recipients = this.getFinalConfirmationEmailRecipients();
    if (!recipients || recipients.length == 0) {
      this.toastService.addError("Error", "You have to select at least one email address");
      this.sendingBookingReportByEmail = false;
      return;
    }
    let message = new GmailMessageWithBookingFiles(
      recipients,
      this.bookingFinalConfirmationEmailForm.value.subject,
      this.bookingFinalConfirmationEmailForm.value.body,
      this.bookingEmailService.emailAttachmentFiles,
      this.bookingFinalConfirmationEmailForm.value.signature.language.id,
      this.bookingService.booking.id!,
      this.bookingFinalConfirmationEmailForm.value.withVivawalletPaymentLink,
      null
    );
    message.body = this.getStringWithEscapedDollar(message.body);
    this.httpService.sendBookingFinalConfirmationByGmail(message, confirmation.id, this.bookingService.booking.id!,
      this.bookingFinalConfirmationEmailForm.value.withPastePixel)
      .subscribe(() => {
        this.toastService.addSuccess("Success", "Mail sent");
        this.displayEmailFormWithBookingFinalConfirmation = false;
        this.sendingBookingConfirmationByEmail = false;
        this.bookingEmailService.emailAttachmentFiles = [];
        this.bookingFinalConfirmations[0].lastSent = moment().utc(true).toString();
        this.bookingFinalConfirmations[0].lastSentBy = this.userService.userName!;
        this.bookingService.bookingForm.get("finalConfirmationSent")!.setValue(moment().toDate());
        this.sendingBookingReportByEmail = false;
      }, error => {
        this.sendingBookingReportByEmail = false;
        this.sendingBookingConfirmationByEmail = false;
        this.toastService.addError("Error", error.error);
        this.bookingEmailService.emailAttachmentFiles = [];
      });
  }

  activatePreConfirmationForm() {
    this.bookingViewModeService.selectedView = this.bookingViewModeService.PRE_CONFIRMATION_VIEW;
  }

  sendReportByMail() {
    this.sendingBookingReportByEmail = true;
    let recipients = this.getEmailRecipients();
    if (!recipients || recipients.length == 0) {
      this.toastService.addError("Error", "You have to select at least one email address");
      this.sendingBookingReportByEmail = false;
      return;
    }
    let message = new GmailMessageWithBookingFiles(
      recipients,
      this.bookingReportEmailForm.value.subject,
      this.bookingReportEmailForm.value.body,
      this.bookingEmailService.emailAttachmentFiles,
      null,
      this.bookingService.booking.id!,
      this.bookingReportEmailForm.value.withVivawalletPaymentLink,
      null
    );
    message.body = this.getStringWithEscapedDollar(message.body);
    this.httpService.sendBookingReportByGmail(message, this.bookingReportEmailForm.value.report.id, this.bookingReportEmailForm.value.withPastePixel)
      .subscribe(() => {
        this.toastService.addSuccess("Success", "Mail sent");
        this.displayEmailForm = false;
        this.sendingBookingReportByEmail = false;
        this.bookingEmailService.emailAttachmentFiles = [];
        this.bookingReportEmailForm.value.report.sent = moment();
        this.bookingReportEmailForm.value.report.sentBy = new SystemUser(this.userService.userName, this.userService.roles!);
        this.bookingService.bookingForm.get("sentToSchool")!.setValue(new Date());
      }, error => {
        this.sendingBookingReportByEmail = false;
        this.toastService.addError("Error", error.error);
        this.bookingEmailService.emailAttachmentFiles = [];
      });
  }

  initGlobalFilesImportForm() {
    this.globalFilesImportForm = [];
    for (let file of this.globalFiles) {
      let form = this.builder.group({
        data: file,
        importThisFile: false
      });
      this.globalFilesImportForm.push(form);
    }
  }

  importGlobalFiles() {
    let globalFilesToImport = [];
    for (let form of this.globalFilesImportForm) {
      if (form.value.importThisFile) {
        globalFilesToImport.push(form.value.data);
      }
    }
    if (globalFilesToImport.length > 0) {
      this.httpService.importGlobalFilesToBooking(globalFilesToImport, this.bookingService.booking.id!).subscribe(() => {
        this.toastService.addSuccess("Success", "Files imported");
        this.activeGlobalFileImportDialog = false;
        this.globalFiles = [];
        this.globalFilesImportForm = [];
        this.updateBookingGlobalFiles();
      }, error => this.toastService.addError("Error", error.error)
      );
    }
  }

  openGlobalFilesImportDialog() {
    this.activeGlobalFileImportDialog = true;
  }

  initGlobalFileImportDialog() {
    this.loadingGlobalFiles = true;
    this.httpService.getGlobalBookingFiles().subscribe(res => {
      this.loadingSchoolFiles = false;
      this.globalFiles = res;
      this.initGlobalFilesImportForm();
    }, error => {
      this.toastService.addError("Error", error.error);
      this.loadingGlobalFiles = false;
    }
    );
  }

  updateBookingGlobalFiles() {
    this.httpService.getBookingGlobalFiles(this.bookingService.booking.id!).subscribe(res => {
      this.importedGlobalFiles = res;
    });
  }

  downloadIndividualFile(file: BookingFile) {
    this.httpService.getBookingFileById(file.id!).subscribe(res => {
      let binaryData = [];
      binaryData.push(res);
      let downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'blob' }));
      downloadLink.setAttribute('download', file.fileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
    });
  }

  updateSchoolFile(schoolFile: BookingSchoolFile) {
    this.httpService.updateBookingSchoolFile(schoolFile).subscribe(() => {
      this.toastService.addSuccess("Success", "file updated");
    }, error => {
      this.toastService.addError("Error", error.error);
    });
  }

  deleteImportedSchoolFile(schoolFile: BookingSchoolFile) {
    this.confirmationService.confirm({
      message: "Are you sure to delete file: " + schoolFile.fileName + "?",
      accept: () => {
        this.httpService.deleteBookingSchoolFile(schoolFile.id!).subscribe(() => {
          this.updateSchoolFiles();
          this.toastService.addSuccess("Success", "file deleted");
        }, error => this.toastService.addError("Error", error.error));
      }
    });
  }

  addEmptyIndividualFile() {
    this.httpService.postEmptyFileForBooking(this.bookingService.booking.id!, this.selectedFileType!).subscribe(() => {
      this.toastService.addSuccess("Success", "Empty file added");
      this.updateIndividualFiles();
      this.creatingEmptyIndividualFile = false;
      this.selectedFileType = null;
    }, error => this.toastService.addError("Error", error.error));
  }

  uploadIndividualFile(files: File[], form: any) {
    if (files.length == 0) {
      return;
    }
    const resultObs: Observable<void>[] = [];
    for(let file of files){
      resultObs.push(this.httpService.postFileForBooking(this.bookingService.booking.id!, file));
    }
    forkJoin(resultObs).subscribe(() => {
      this.fileToUpload = null;
      this.toastService.addSuccess("Success", "File uploaded");
      this.updateIndividualFiles();
      form.clear();
    }, error => this.toastService.addError("Error", error.error));
  }

  updateIndividualFile(file: BookingFile) {
    this.httpService.updateBookingFile(file).subscribe(() => {
      this.toastService.addSuccess("Success", "file updated");
    }, error => {
      this.toastService.addError("Error", error.error);
    });
  }

  deleteImportedIndividualFile(file: BookingFile) {
    this.confirmationService.confirm({
      message: "Are you sure to delete file: " + file.fileName + "?",
      accept: () => {
        this.httpService.deleteBookingFileById(file.id!).subscribe(() => {
          this.updateIndividualFiles();
          this.toastService.addSuccess("Success", "file deleted");
        }, error => this.toastService.addError("Error", error.error));
      }
    });
  }

  downloadGlobalFile(file: BookingGlobalFile) {
    this.httpService.getBookingGlobalFileDataById(file.id!).subscribe(res => {
      let binaryData = [];
      binaryData.push(res);
      let downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'blob' }));
      downloadLink.setAttribute('download', file.fileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
    });
  }

  downloadSchoolFile(file: BookingSchoolFile) {
    this.httpService.getBookingSchoolFileDataById(file.id!).subscribe(res => {
      let binaryData = [];
      binaryData.push(res);
      let downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'blob' }));
      downloadLink.setAttribute('download', file.fileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
    });
  }

  updateIndividualFiles() {
    this.httpService.getBookingFiles(this.bookingService.booking.id!).subscribe(res => {
      this.individualBookingFiles = res;
    });
  }

  updateSchoolFiles() {
    this.httpService.getBookingSchoolFiles(this.bookingService.booking.id!).subscribe(res => {
      this.importedSchoolFiles = res;
    });
  }

  updateGlobalFile(globalFile: BookingGlobalFile) {
    this.httpService.updateBookingGlobalFile(globalFile).subscribe(() => {
      this.toastService.addSuccess("Success", "file updated");
    }, error => this.toastService.addError("Error", error.message));
  }

  deleteImportedGlobalFile(globalFile: BookingGlobalFile) {
    this.confirmationService.confirm({
      message: "Are you sure to delete file: " + globalFile.fileName + "?",
      accept: () => {
        this.httpService.deleteBookingGlobalFile(globalFile.id!).subscribe(() => {
          this.updateBookingGlobalFiles();
          this.toastService.addSuccess("Success", "file deleted");
        }, error => this.toastService.addError("Error", error.message));
      }
    });
  }

  openSchoolFilesImportDialog() {
    this.activeSchoolFileImportDialog = true;
  }

  openSchoolFilesToBeCompletedImportDialog() {
    this.activeSchoolFileToBeCompletedImportDialog = true;
  }

  initSchoolFileImportDialog() {
    this.loadingSchoolFiles = true;
    this.httpService.getSchoolFilesBySchoolId(this.bookingService.booking.school.id!).subscribe(res => {
      this.loadingSchoolFiles = false;
      this.schoolFiles = res;
      this.initSchoolFilesImportForm();
    }, error => {
      this.toastService.addError("Error", error.message);
      this.loadingSchoolFiles = false;
    }
    );
  }

  initSchoolFilesImportForm() {
    this.schoolFilesImportForm = [];
    for (let file of this.schoolFiles) {
      let form = this.builder.group({
        data: file,
        importThisFile: false
      });
      this.schoolFilesImportForm.push(form);
    }
  }

  importSchoolFiles() {
    let schoolFilesToImport = [];
    for (let form of this.schoolFilesImportForm) {
      if (form.value.importThisFile) {
        schoolFilesToImport.push(form.value.data);
      }
    }
    if (schoolFilesToImport.length > 0) {
      this.httpService.importSchoolFilesToBooking(schoolFilesToImport, this.bookingService.booking.id!).subscribe(() => {
        this.toastService.addSuccess("Success", "Files imported");
        this.activeSchoolFileImportDialog = false;
        this.schoolFiles = [];
        this.schoolFilesImportForm = [];
        this.updateSchoolFiles();
      }, error => this.toastService.addError("Error", error.message)
      );
    }
  }

  importSchoolFilesToBeCompleted() {
    let schoolFilesToImport = [];
    for (let form of this.schoolFilesImportForm) {
      if (form.value.importThisFile) {
        schoolFilesToImport.push(form.value.data);
      }
    }
    if (schoolFilesToImport.length > 0) {
      this.httpService.importSchoolFilesToBooking(schoolFilesToImport, this.bookingService.booking.id!).subscribe(() => {
        this.toastService.addSuccess("Success", "Files imported");
        this.activeSchoolFileImportDialog = false;
        this.schoolFiles = [];
        this.schoolFilesImportForm = [];
      }, error => this.toastService.addError("Error", error.message)
      );
    }
  }

  reportDocumentTypeChange(value: any) {
    if (value.content instanceof BookingFinalConfirmationTemplate) {
      this.isFinalConfirmationTemplateSelected = true;
    } else {
      this.isFinalConfirmationTemplateSelected = false;
    }
  }

  editFinalConf() {
    this.editableFinalConfirmationHtmlString = null;
    this.activeFinalConfEdit = true;
    this.httpService.getBookingFinalConfirmationTemplateById(this.selectedDocumentTemplate.content.id!).subscribe(res => {
      this.editableFinalConfirmationHtmlString = res.body;
      console.log(res.body.split("\n"));
    });
  }

  generateModifiedFinalConf() {
    this.activeFinalConfEdit = false;
    this.httpService.postModifiedFinalConfirmation(this.editableFinalConfirmationHtmlString!, this.selectedDocumentTemplate.content.id!, this.bookingService.booking.id!).subscribe(() => {
      this.toastService.addSuccess("Success", "Final confirmation created");
      this.makingBookingReport = false;
      this.editableFinalConfirmationHtmlString = null;
      this.httpService.getBookingFinalConfirmations(this.bookingService.booking.id!).subscribe(res => {
        this.bookingService.bookingFinalConfirmations = res;
        this.initBookingFinalConfirmations();
      });
    }, error => {
      this.toastService.addError("Error", error.message);
      this.makingBookingReport = false;
    });
  }

  generateBookingDocument() {
    if (!this.selectedDocumentTemplate) {
      return;
    }
    this.makingBookingReport = true;
    if (this.selectedDocumentTemplate.content instanceof BookingReportTemplate) {
      this.httpService.postBookingReport(this.selectedDocumentTemplate.content, this.bookingService.booking.id!).subscribe(() => {
        this.toastService.addSuccess("Success", "Success");
        this.makingBookingReport = false;
        this.httpService.getBookingReportsByBookingId(this.bookingService.booking.id!).subscribe(reports => {
          this.bookingService.bookingReports = reports;
        });
      }, error => {
        this.makingBookingReport = false;
        this.toastService.addError("Error", error.message);
      });
    } else if (this.selectedDocumentTemplate.content instanceof BookingConfirmationTemplate) {
      this.httpService.postBookingConfirmation(this.selectedDocumentTemplate.content, this.bookingService.booking.id!).subscribe(() => {
        this.toastService.addSuccess("Success", "Booking confimation created");
        this.makingBookingReport = false;
        this.httpService.getBookingConfirmations(this.bookingService.booking.id!).subscribe(res => {
          this.bookingService.bookingConfirmations = res;
          this.initBookingConfirmations();
        });
      }, error => {
        this.toastService.addError("Error", error.message);
        this.makingBookingReport = false;
      });
    } else if (this.selectedDocumentTemplate.content instanceof BookingFinalConfirmationTemplate) {
      this.httpService.postBookingFinalConfirmation(this.selectedDocumentTemplate.content, this.bookingService.booking.id!).subscribe(() => {
        this.toastService.addSuccess("Success", "Final confirmation created");
        this.makingBookingReport = false;
        this.httpService.getBookingFinalConfirmations(this.bookingService.booking.id!).subscribe(res => {
          this.bookingService.bookingFinalConfirmations = res;
          this.initBookingFinalConfirmations();
        });
      }, error => {
        this.toastService.addError("Error", error.message);
        this.makingBookingReport = false;
      });
    }
  }

  deleteBookingReport(report: BookingReport) {
    this.confirmationService.confirm({
      message: "Are you sur to delete this report: " + report.fileName + "?",
      accept: () => {
        this.httpService.deleteBookingReport(report.id).subscribe(() => {
          this.toastService.addSuccess("Success", "Deleted successfully");
          this.bookingService.bookingReports = this.bookingService.bookingReports.filter(item => {
            return item.id != report.id;
          });
        }, error => {
          this.toastService.addError("Error", error.message);
        });
      }
    });
  }

  deleteBookingConfirmation(confirmation: BookingConfirmation) {
    this.confirmationService.confirm({
      message: "Are you sur to delete this confirmation: " + confirmation.id + "?",
      accept: () => {
        this.httpService.deleteBookingConfirmation(confirmation.id).subscribe(() => {
          this.toastService.addSuccess("Success", "Deleted successfully");
          this.bookingConfirmations = this.bookingConfirmations.filter(item => {
            return item.id != confirmation.id;
          });
        }, error => {
          this.toastService.addError("Error", error.message);
        });
      }
    });
  }

  deleteBookingFinalConfirmation(finalConfirmation: BookingFinalConfirmation) {
    this.confirmationService.confirm({
      message: "Are you sur to delete this final confirmation: " + finalConfirmation.id + "?",
      accept: () => {

        this.httpService.deleteBookingFinalConfirmation(finalConfirmation.id).subscribe(() => {
          this.toastService.addSuccess("Success", "Deleted successfully");
          this.bookingFinalConfirmations = this.bookingFinalConfirmations.filter(item => {
            return item.id != finalConfirmation.id;
          });
        }, error => {
          this.toastService.addError("Error", error.message);
        });
      }
    });
  }

  downloadReport(report: BookingReport) {
    this.processing = true;
    this.httpService.getBookingReportPdfById(report.id).subscribe(blob => {
      let pdfUrl = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = pdfUrl;
      link.download = report.fileName + ".pdf";
      link.click();
      this.processing = false;
    });
  }

  downloadPreConfirmationPdf(preconf: SimpleBookingPreConfirmation) {
    this.processing = true;
    forkJoin({
      fileName: this.httpService.getBookingPreConfirmationFileName(preconf.id),
      preConfFileData: this.httpService.getBookingPreConfirmationPdfBlob(preconf.id)
    }).subscribe(({ fileName, preConfFileData }) => {
      let pdfUrl = window.URL.createObjectURL(preConfFileData);
      let link = document.createElement("a");
      if (fileName == null || fileName == "") {
        link.download = this.generateDownloadedPdfFileName("Pré-confirmation");
      } else {
        link.download = fileName;
      }
      link.href = pdfUrl;
      link.click();
      this.processing = false;
    });
  }

  downloadConfirmationPdf(confirmation: BookingConfirmation) {
    this.processing = true;
    console.log(confirmation);
    this.httpService.getBookingConfirmationPdfBlob(confirmation.id).subscribe(blob => {
      let pdfUrl = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = pdfUrl;
      link.download = this.generateDownloadedPdfFileName(confirmation.originalTemplateName);
      link.click();
      this.processing = false;
    });
  }

  generateDownloadedPdfFileName(templateName: string) {
    return templateName + "_" + this.bookingService.booking.student.lastName.trim().toUpperCase() + ".pdf";
  }

  downloadFinalConfirmationPdf(finalConfirmation: BookingFinalConfirmation) {
    this.processing = true;
    this.httpService.getBookingFinalConfirmationPdfBlob(finalConfirmation.id).subscribe(blob => {
      let pdfUrl = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = pdfUrl;
      link.download = this.generateDownloadedPdfFileName(finalConfirmation.originalTemplateName);
      link.click();
      this.processing = false;
    });
  }

  deletePreConfirmation(preconfId: number) {
    this.confirmationService.confirm({
      message: "Are you sure to delete pre confirmation: " + preconfId + "?",
      accept: () => {
        this.httpService.deleteBookingPreConfirmation(preconfId).subscribe(() => {
          this.getPreConfirmations();
        }, error => this.toastService.addError("Error", error.error));
      }
    });
  }

  getPreConfirmations() {
    this.httpService.getSimpleBookingPreConfirmationsByBookingId(this.bookingService.booking.id!).subscribe(res => {
      this.bookingService.preConfirmations = res;
    });
  }

  downloadSchoolResponseFile(schoolFile: BookingSchoolFile) {
    this.httpService.getBookingSchoolFileResponseById(schoolFile.responseFileId).subscribe(res => {
      let binaryData = [];
      binaryData.push(res.body!);
      this.downloadFile(binaryData, schoolFile.responseFileName);
    });
  }

  downloadGlobalResponseFile(globalFile: BookingGlobalFile) {
    this.httpService.getGlobalFileResponseById(globalFile.responseFileId).subscribe(res => {
      let binaryData = [];
      binaryData.push(res.body!);
      this.downloadFile(binaryData, globalFile.responseFileName);
    });
  }

  downloadIndividualResponseFile(individualFile: BookingFile) {
    this.httpService.getIndividualFileResponseById(individualFile.responseFileId).subscribe(res => {
      let binaryData = [];
      binaryData.push(res.body!);
      this.downloadFile(binaryData, individualFile.responseFileName);
    });
  }

  downloadFile(bytes: BlobPart[], fileName: string) {
    let downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(new Blob(bytes, { type: 'blob' }));
    downloadLink.setAttribute('download', fileName);
    document.body.appendChild(downloadLink);
    downloadLink.click();
  }

  openEmptyIndividualFileCreationDialog() {
    this.creatingEmptyIndividualFile = true;
  }

  updateBookingInformation() {
    this.httpService.getBookingById(this.bookingService.booking.id!).subscribe(booking => {
      this.bookingService.booking = booking;
      this.bookingService.updateBookingForm();
    });
  }

  compareId(a: FileType, b: FileType) {
    return a && b ? a.id === b.id : a === b;
  }

  onVivawalletChange(event: any, form: FormGroup) {
    this.updateVivawalletChargeAmount(event.checked, form);
  }

  //formGroup should have withVivawalletPaymentLink that should be boolean
  updateVivawalletChargeAmount(checked: boolean, formGroup: FormGroup) {
    if (this.bookingService.booking.preConfirmation!.selectedBank.id == 2) {
      formGroup.get("withVivawalletPaymentLink")!.setValue(false);
      return;
    }
    if (checked) {
      this.httpService.getVivawalletPaymentOrders(this.bookingService.booking.id!).subscribe(orders => {
        for (let order of orders) {
          if (order.isExpired == false && order.isPaid == false) {
            this.toastService.addError("Error", "This booking has already payment order");
            this.hasVivawalletPaymentOrder = true;
            formGroup.get("withVivawalletPaymentLink")!.setValue(false);
            this.vivawalletCheckourUrl = this.getCheckoutUrl(order.orderCode);
          }
        }
        this.httpService.getVivawalletOrderInformation(this.bookingService.booking.id!).subscribe(res => {
          if (res == null) {
            this.toastService.addError("Error", "Vivawallet checkout doesn't support currency conversion yet.");
            formGroup.get("withVivawalletPaymentLink")!.setValue(false);
            return;
          }
          let dueAmount = new Decimal(res.amount).div(100);
          if (dueAmount.comparedTo(10) < 0) {
            this.toastService.addError("Error", "Remaining balance is too small for vivawallet checkout");
            formGroup.get("withVivawalletPaymentLink")!.setValue(false);
            return;
          }
          this.vivawalletOrderAmount = dueAmount;
        }, error => {
          this.toastService.addError("Error", error.error);
        });
      });
    } else {
      this.vivawalletOrderAmount = null;
    }
  }

  getCheckoutUrl(orderCode: string) {
    let url = environment.vivawalletCheckoutBaseUrl + "?ref=" + orderCode + "&color=1F89CA&lang=" + this.getVivawalletLanguageStr();
    return url;
  }

  getVivawalletLanguageStr() {
    if (this.bookingService.booking.student.language == null) {
      return "fr-FR";
    }
    switch (this.bookingService.booking.student.language.id) {
      case 2:
        return "en-US";
      case 3:
        return "nl-NL";
      case 4:
        return "es-ES";
      case 5:
        return "pt-BR";
      default:
        return "fr-FR";
    }
  }

}